import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody, ErrorBlock } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Settings as SettingsIcon, Check, X, Save, Wand2, Link2, Package,
  Video as VideoIcon, Key, Shield, ShoppingBag, Copy, Store, ExternalLink,
  Inbox as InboxIcon, Send, Loader2, AlertTriangle, Music,
} from "lucide-react";

const CONNECTOR_FIELDS = {
  amazon: { label: "Amazon Associates", required: ["AMAZON_ASSOCIATE_TAG"], optional: ["AMAZON_PAAPI_ACCESS_KEY", "AMAZON_PAAPI_SECRET_KEY"] },
  aliexpress: { label: "AliExpress Affiliate", required: ["ALIEXPRESS_PID"], optional: ["ALIEXPRESS_APP_KEY", "ALIEXPRESS_APP_SECRET"] },
  clickbank: { label: "ClickBank", required: ["CLICKBANK_NICKNAME"], optional: ["CLICKBANK_API_KEY"] },
  tiktok_shop: { label: "TikTok Shop Affiliate", required: ["TIKTOK_SHOP_APP_KEY", "TIKTOK_SHOP_APP_SECRET"], optional: ["TIKTOK_SHOP_ACCESS_TOKEN", "TIKTOK_SHOP_SHOP_ID"] },
};

const VIDEO_PROVIDERS = [
  { key: "HEYGEN_API_KEY", label: "HeyGen (avatar)", icon: Wand2 },
  { key: "VEO_API_KEY", label: "Google Veo (generative)", icon: VideoIcon },
  { key: "RUNWAY_API_KEY", label: "Runway (generative)", icon: VideoIcon },
  { key: "OPENAI_SORA_API_KEY", label: "OpenAI Sora (generative)", icon: VideoIcon },
];

export default function SettingsPage() {
  const [state, setState] = useState(null);
  const [edits, setEdits] = useState({}); // {KEY_NAME: newValue}
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings`);
      setState(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data || { error: "load_failed", detail: String(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setEdit = (k, v) => setEdits((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (Object.keys(edits).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/settings`, edits);
      setState(r.data);
      setEdits({});
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testConn = async (name) => {
    setTesting((t) => ({ ...t, [name]: true }));
    try {
      const r = await axios.post(`${API}/settings/test/${name}`);
      if (r.data?.ok || r.data?.configured) {
        toast.success(`${name}: OK${r.data.quota ? ` · ${r.data.quota.remaining_seconds}s left` : ""}`);
      } else {
        toast.error(`${name}: ${r.data?.detail || "not configured"}`);
      }
    } catch (e) {
      toast.error(`${name}: ${formatApiError(e?.response?.data?.detail) || "test failed"}`);
    } finally {
      setTesting((t) => ({ ...t, [name]: false }));
    }
  };

  if (err) return <PageBody><ErrorBlock data={err} testid="settings-error" /></PageBody>;
  if (!state) return <PageBody><div className="text-sm text-zinc-500">Loading…</div></PageBody>;

  const keys = state.keys || {};
  const valueFor = (k) => (k in edits ? edits[k] : "");
  const displayMasked = (k) => keys[k]?.masked || (keys[k]?.configured ? "configured" : "—");

  const KeyRow = ({ k, isRequired = true }) => (
    <div className="flex items-center gap-2 py-1.5" data-testid={`key-row-${k}`}>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] mono text-zinc-500 uppercase truncate">{k}{isRequired && <span className="text-rose-400 ml-1">*</span>}</div>
        <div className="text-[10px] mono text-zinc-600">current: <span className={keys[k]?.configured ? "text-emerald-400" : "text-zinc-500"}>{displayMasked(k)}</span></div>
      </div>
      <Input
        type={valueFor(k) || !keys[k]?.configured ? "text" : "password"}
        placeholder={keys[k]?.configured ? "(unchanged)" : "set value…"}
        value={valueFor(k)}
        onChange={(e) => setEdit(k, e.target.value)}
        className="flex-1 bg-zinc-900 border-zinc-700 text-zinc-100 text-xs"
        data-testid={`key-input-${k}`}
      />
      {k in edits && (
        <Button onClick={() => setEdits((e) => { const n = { ...e }; delete n[k]; return n; })}
                variant="ghost" size="sm" className="text-zinc-500 hover:text-rose-400"
                data-testid={`key-clear-${k}`}>
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        icon={SettingsIcon}
        title="Settings"
        subtitle="Admin · Affiliate connector keys + video providers"
        testid="settings-header"
        actions={
          <Button onClick={save} disabled={saving || Object.keys(edits).length === 0}
                  className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                  data-testid="settings-save-btn">
            <Save className="h-4 w-4 mr-2" /> Save {Object.keys(edits).length > 0 && `(${Object.keys(edits).length})`}
          </Button>
        }
      />

      <PageBody>
        {/* Deployment / redirect-URL audit banner (Phase 4.4) */}
        <DeploymentBanner />

        {/* TikTok connected accounts (Phase 5.1) */}
        <TikTokAccountsSection />

        {/* Inbox (Resend inbound) */}
        <InboxAddressesSection />

        {/* TikTok Shop OAuth + Active Shop */}
        <TikTokShopSection />

        {/* Public Storefront */}
        <StorefrontSection />

        {/* Connectors */}
        <section className="ap-panel p-5" data-testid="connectors-section">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="h-4 w-4 text-emerald-400" />
            <h2 className="text-sm font-semibold">Affiliate Connectors</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(state.connectors || []).map((c) => {
              const tagOnly = c.tag_only_configured && !c.api_configured;
              const fullyConfigured = c.api_configured;
              return (
              <div key={c.name} className="ap-panel-2 p-4 space-y-3" data-testid={`connector-${c.name}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 text-zinc-400 shrink-0" />
                    <span className="text-sm font-medium truncate">{c.display_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline"
                           className={c.tag_only_configured
                             ? "border-emerald-700/60 text-emerald-300 bg-emerald-500/5 text-[10px]"
                             : "border-zinc-700 text-zinc-500 text-[10px]"}
                           data-testid={`connector-tag-status-${c.name}`}
                           title="Has the basic tracking ID — enables auto-tag on saved URLs.">
                      {c.tag_only_configured ? <><Check className="h-2.5 w-2.5 mr-0.5" /> tag</> : "tag ✕"}
                    </Badge>
                    <Badge variant="outline"
                           className={fullyConfigured
                             ? "border-emerald-700/60 text-emerald-300 bg-emerald-500/5 text-[10px]"
                             : "border-zinc-700 text-zinc-500 text-[10px]"}
                           data-testid={`connector-api-status-${c.name}`}
                           title="Has full API credentials — enables product search + import.">
                      {fullyConfigured ? <><Check className="h-2.5 w-2.5 mr-0.5" /> api</> : "api ✕"}
                    </Badge>
                  </div>
                </div>
                {tagOnly && (
                  <div className="text-[10px] mono text-amber-300/80 -mt-1">
                    configured (tag only — API search disabled, waiting on {(c.optional_env_vars[0] || "API key")})
                  </div>
                )}
                <div className="space-y-1">
                  {c.required_env_vars.map((k) => <KeyRow key={k} k={k} isRequired />)}
                  {c.optional_env_vars.length > 0 && (
                    <div className="pt-1.5 border-t border-zinc-800">
                      <div className="text-[10px] mono text-zinc-600 uppercase mb-1">optional (api)</div>
                      {c.optional_env_vars.map((k) => <KeyRow key={k} k={k} isRequired={false} />)}
                    </div>
                  )}
                </div>
                <Button onClick={() => testConn(c.name)} disabled={testing[c.name]}
                        variant="outline" size="sm"
                        className="w-full border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
                        data-testid={`connector-test-${c.name}`}>
                  {testing[c.name] ? "Testing…" : "Test connection"}
                </Button>
              </div>
            );})}
          </div>
        </section>

        {/* Video Providers */}
        <section className="ap-panel p-5" data-testid="providers-section">
          <div className="flex items-center gap-2 mb-4">
            <VideoIcon className="h-4 w-4 text-amber-400" />
            <h2 className="text-sm font-semibold">Video Providers</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {VIDEO_PROVIDERS.map((p) => {
              const Icon = p.icon;
              const cfg = keys[p.key]?.configured;
              return (
                <div key={p.key} className="ap-panel-2 p-4 space-y-3" data-testid={`provider-${p.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-zinc-400" />
                      <span className="text-sm font-medium">{p.label}</span>
                    </div>
                    <Badge variant="outline"
                           className={cfg ? "border-emerald-700/60 text-emerald-300 bg-emerald-500/5"
                                          : "border-zinc-700 text-zinc-500"}>
                      {cfg ? <><Check className="h-3 w-3 mr-1" /> configured</> : "not configured"}
                    </Badge>
                  </div>
                  <KeyRow k={p.key} isRequired />
                  {p.key === "HEYGEN_API_KEY" && (
                    <Button onClick={() => testConn("heygen")} disabled={testing.heygen}
                            variant="outline" size="sm"
                            className="w-full border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
                            data-testid="provider-test-heygen">
                      {testing.heygen ? "Testing…" : "Test connection (shows quota)"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <div className="text-[10px] mono text-zinc-600 flex items-center gap-2">
          <Shield className="h-3 w-3" /> Keys are stored in Mongo (`app_settings`). The .env file is read as fallback only.
        </div>
      </PageBody>
    </>
  );
}

// ----------------- Deployment audit banner (Phase 4.4) -----------------
function DeploymentBanner() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/config/deployment-status`);
        if (!cancelled) setStatus(r.data);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status?.needs_redirect_update) return null;

  return (
    <section className="rounded-md border border-amber-700/60 bg-amber-950/30 p-4"
             data-testid="deployment-redirect-banner">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-200">
            Update redirect URLs in third-party dashboards
          </div>
          <div className="text-xs text-amber-200/80 mt-1 mb-3">
            <span className="mono">PUBLIC_BASE_URL</span> is now{" "}
            <span className="mono text-amber-100">{status.public_base_url}</span>.
            Make sure each external integration points at the production callback below.
          </div>
          <div className="space-y-1.5">
            {status.callbacks.map((c) => (
              <div key={c.key} className="flex items-start gap-2 text-xs flex-wrap"
                   data-testid={`redirect-row-${c.key}`}>
                <span className="text-zinc-400 w-44 shrink-0">{c.label}</span>
                <code className="flex-1 min-w-0 mono text-emerald-300 break-all">{c.url}</code>
                <a href={c.fix_url} target="_blank" rel="noreferrer"
                   className="text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
                   data-testid={`redirect-fix-${c.key}`}>
                  Open dashboard <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ))}
          </div>
          {!status.tiktok_token_set && (
            <div className="text-[11px] text-amber-200/70 mt-3 mono">
              TIKTOK_DOMAIN_VERIFICATION_TOKEN is not set — paste the token from
              developers.tiktok.com into backend/.env so `/.well-known/tiktok-developers.txt` returns it.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ----------------- Inbox addresses section (Phase 4.3) -----------------
function InboxAddressesSection() {
  const [addrs, setAddrs] = useState([]);
  const [domain, setDomain] = useState("");
  const [resendOk, setResendOk] = useState(true);
  const [testing, setTesting] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inbox/addresses`);
      setAddrs(r.data.addresses || []);
      setDomain(r.data.domain || "");
      setResendOk(!!r.data.resend_configured);
    } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (s) => {
    navigator.clipboard.writeText(s);
    toast.success(`Copied ${s}`);
  };

  const sendTest = async (address) => {
    setTesting(address);
    try {
      const r = await axios.post(`${API}/inbox/test-email`, { address });
      // Log to console so prod issues can be diagnosed from devtools.
      // eslint-disable-next-line no-console
      console.info("[inbox-test] sent", r.data);
      toast.success(
        `Test sent to ${address} (Resend id ${(r.data.sent_id || "").slice(0, 8)}…). Watch the Inbox — it should arrive in ~60s.`,
        { duration: 10000 }
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[inbox-test] failed", e?.response?.status, e?.response?.data);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : formatApiError(detail);
      toast.error(`Send failed (HTTP ${e?.response?.status || "?"}): ${msg || "unknown error"}`, { duration: 12000 });
    } finally { setTesting(null); }
  };

  const editLabel = async (id, current) => {
    const next = window.prompt("New label:", current);
    if (!next || next === current) return;
    try {
      await axios.patch(`${API}/inbox/addresses/${id}`, { label: next });
      toast.success("Label updated");
      load();
    } catch (_) { toast.error("Failed"); }
  };

  return (
    <section className="ap-panel p-5" data-testid="inbox-addresses-section">
      <div className="flex items-center gap-2 mb-4">
        <InboxIcon className="h-4 w-4 text-emerald-400" />
        <h2 className="text-sm font-semibold">
          Inbox <span className="text-zinc-500 font-normal">· Resend inbound on {domain || "—"}</span>
        </h2>
      </div>
      {!resendOk && (
        <div className="text-xs text-amber-200 mb-3 mono">
          ! RESEND_API_KEY or RESEND_WEBHOOK_SECRET not set — inbound delivery disabled.
        </div>
      )}
      <div className="space-y-2">
        {addrs.map((a) => (
            <div key={a.id} className="ap-panel-2 p-3 space-y-2"
                 data-testid={`inbox-addr-${a.address}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => editLabel(a.id, a.label)}
                        className="text-sm font-medium text-zinc-100 hover:text-emerald-300"
                        data-testid={`inbox-addr-label-${a.address}`}>
                  {a.label}
                </button>
                {a.tiktok_account_handle && (
                  <span className="text-[10px] mono text-zinc-500">@{a.tiktok_account_handle}</span>
                )}
                {a.unread_count > 0 && (
                  <Badge variant="outline" className="border-emerald-700/60 text-emerald-300 bg-emerald-500/10 text-[10px]">
                    {a.unread_count} unread
                  </Badge>
                )}
              </div>
              <div className="text-[11px] mono text-emerald-400 truncate">{a.address}</div>
              <div className="flex items-center gap-2 flex-col sm:flex-row">
                <Button size="sm" variant="outline" onClick={() => copy(a.address)}
                        className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs w-full sm:w-auto"
                        data-testid={`inbox-addr-copy-${a.address}`}>
                  <Copy className="h-3 w-3 mr-1.5" /> Copy address
                </Button>
                <Button size="sm" onClick={() => sendTest(a.address)}
                        disabled={testing === a.address}
                        className="bg-blue-500 hover:bg-blue-400 text-zinc-950 font-semibold text-xs w-full sm:w-auto"
                        data-testid={`inbox-test-btn-${a.address}`}>
                  {testing === a.address
                    ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Sending…</>
                    : <><Send className="h-3 w-3 mr-1.5" /> Send test email</>}
                </Button>
              </div>
            </div>
        ))}
        {addrs.length === 0 && (
          <div className="text-xs text-zinc-500">No addresses registered yet.</div>
        )}
      </div>
      <div className="text-[10px] mono text-zinc-600 mt-3">
        Resend webhook URL: <span className="text-emerald-400">{`{REACT_APP_BACKEND_URL}/api/webhooks/resend/inbound`}</span>
      </div>
    </section>
  );
}

// ----------------- TikTok Shop OAuth section -----------------
function TikTokShopSection() {
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/affiliate/tiktok-shop/authorizations`);
      setAuths(r.data.items || []);
    } catch (_) {}
  }, []);
  useEffect(() => {
    load();
    // After OAuth callback redirect, ?tts_connected=1 is set
    if (new URLSearchParams(window.location.search).get("tts_connected") === "1") {
      toast.success("TikTok Shop connected");
      window.history.replaceState({}, "", "/app/settings");
    }
  }, [load]);

  const connect = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/affiliate/tiktok-shop/auth/start`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error("Could not start OAuth flow");
      setLoading(false);
    }
  };

  const disconnect = async (id) => {
    if (!window.confirm("Disconnect this TikTok Shop?")) return;
    try {
      await axios.post(`${API}/affiliate/tiktok-shop/authorizations/${id}/disconnect`);
      toast.success("Disconnected");
      load();
    } catch (_) { toast.error("Failed"); }
  };

  const setActive = async (id) => {
    try {
      await axios.post(`${API}/settings/active-shop`, { authorization_id: id });
      toast.success("Active shop switched");
      load();
    } catch (_) { toast.error("Failed"); }
  };

  return (
    <section className="ap-panel p-5" data-testid="tiktok-shop-section">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingBag className="h-4 w-4 text-pink-400" />
        <h2 className="text-sm font-semibold">TikTok Shop Connection</h2>
      </div>
      {auths.length === 0 ? (
        <div className="ap-panel-2 p-4 flex items-center justify-between gap-3">
          <div className="text-xs text-zinc-400">
            Authorize AffiliatePilot to access your TikTok Shop affiliate marketplace, generate shoplinks, and publish videos.
          </div>
          <Button onClick={connect} disabled={loading}
                  className="bg-pink-500 hover:bg-pink-400 text-zinc-950 font-semibold"
                  data-testid="tts-connect-btn">
            <ShoppingBag className="h-4 w-4 mr-2" /> {loading ? "Redirecting…" : "Connect TikTok Shop"}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {auths.map((a) => (
            <div key={a.id} className="ap-panel-2 p-3 flex items-center justify-between gap-2"
                 data-testid={`tts-authorization-${a.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Badge variant="outline"
                       className={a.is_active
                         ? "border-emerald-700/60 text-emerald-300 bg-emerald-500/5"
                         : "border-zinc-700 text-zinc-500"}>
                  {a.is_active ? "active" : "inactive"}
                </Badge>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{a.shop_name || "Unnamed shop"}</div>
                  <div className="text-[10px] mono text-zinc-500">shop_id {a.shop_id} · region {a.region}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {!a.is_active && <Button size="sm" variant="outline" onClick={() => setActive(a.id)}
                        className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                        data-testid={`tts-set-active-${a.id}`}>Set active</Button>}
                <Button size="sm" variant="outline" onClick={() => disconnect(a.id)}
                        className="border-rose-900/60 bg-rose-500/5 text-rose-400 hover:text-rose-300 text-xs"
                        data-testid={`tts-disconnect-${a.id}`}>Disconnect</Button>
              </div>
            </div>
          ))}
          <Button onClick={connect} variant="outline"
                  className="w-full border-pink-700/60 bg-pink-500/5 text-pink-300 text-xs"
                  data-testid="tts-add-shop-btn">
            + Connect another TikTok Shop
          </Button>
        </div>
      )}
    </section>
  );
}

// ----------------- Public storefront section -----------------
function StorefrontSection() {
  const [profile, setProfile] = useState({
    handle: "", display_name: "", bio: "", avatar_url: "",
    niche_tags: [], featured_product_ids: [],
  });
  const [tagInput, setTagInput] = useState("");
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/storefront/profile`).then((r) => {
      setProfile({
        handle: r.data.handle || "",
        display_name: r.data.display_name || "",
        bio: r.data.bio || "",
        avatar_url: r.data.avatar_url || "",
        niche_tags: r.data.niche_tags || [],
        featured_product_ids: r.data.featured_product_ids || [],
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
    // Phase 5.2c-pre — load user's products so featured-picker has options.
    axios.get(`${API}/products`).then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/storefront/profile`, profile);
      setProfile({
        handle: r.data.handle,
        display_name: r.data.display_name,
        bio: r.data.bio || "",
        avatar_url: r.data.avatar_url || "",
        niche_tags: r.data.niche_tags || [],
        featured_product_ids: r.data.featured_product_ids || [],
      });
      toast.success("Storefront updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || profile.niche_tags.includes(t) || profile.niche_tags.length >= 10) {
      setTagInput("");
      return;
    }
    setProfile({ ...profile, niche_tags: [...profile.niche_tags, t] });
    setTagInput("");
  };

  const removeTag = (t) =>
    setProfile({ ...profile, niche_tags: profile.niche_tags.filter((x) => x !== t) });

  const toggleFeatured = (productId) => {
    const set = new Set(profile.featured_product_ids);
    if (set.has(productId)) set.delete(productId);
    else if (set.size < 8) set.add(productId);
    else { toast.error("Max 8 featured products"); return; }
    setProfile({ ...profile, featured_product_ids: Array.from(set) });
  };

  const storeUrl = profile.handle
    ? `${window.location.origin}/store/${profile.handle}` : "";

  if (!loaded) return null;
  return (
    <section className="ap-panel p-5" data-testid="storefront-section">
      <div className="flex items-center gap-2 mb-4">
        <Store className="h-4 w-4 text-emerald-400" />
        <h2 className="text-sm font-semibold">Public Storefront <span className="text-zinc-500 font-normal">(link-in-bio)</span></h2>
      </div>
      {/* Phase 5.3.1 — URL + status banner. Surfaces the exact link the
          operator pastes into their TikTok bio, plus a green/zinc pill
          showing how many products are currently public. */}
      {storeUrl && (
        <div className="ap-panel-2 p-3 mb-3 flex items-center justify-between gap-3 flex-wrap"
             data-testid="storefront-url-banner">
          <div className="min-w-0">
            <div className="text-[10px] mono uppercase text-zinc-500">Your storefront URL</div>
            <a href={storeUrl} target="_blank" rel="noreferrer"
               className="text-sm mono text-emerald-300 hover:text-emerald-200 truncate block"
               data-testid="storefront-url-link">
              {storeUrl}
            </a>
            <div className="text-[10px] text-zinc-500 mt-0.5">Paste this into your TikTok bio.</div>
          </div>
          {(() => {
            const publicCount = (products || []).filter((p) => p.public_storefront).length;
            const isPublic = publicCount > 0;
            return (
              <Badge variant="outline"
                     className={isPublic
                       ? "border-emerald-700/60 bg-emerald-500/10 text-emerald-300 text-[11px]"
                       : "border-zinc-700 text-zinc-400 text-[11px]"}
                     data-testid="storefront-status-pill">
                {isPublic
                  ? `Public — ${publicCount} product${publicCount === 1 ? "" : "s"} visible`
                  : "Private — no products toggled"}
              </Badge>
            );
          })()}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          {/* Avatar row */}
          <div>
            <Label className="text-zinc-300 text-xs">Avatar</Label>
            <div className="flex items-center gap-3 mt-1">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt=""
                     className="h-14 w-14 rounded-full border border-zinc-700 object-cover"
                     data-testid="storefront-avatar-preview" />
              ) : (
                <div className="h-14 w-14 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-600 text-xl">
                  ?
                </div>
              )}
              <Input
                value={profile.avatar_url || ""}
                onChange={(e) => setProfile({ ...profile, avatar_url: e.target.value })}
                placeholder="https://… (paste an image URL)"
                className="bg-zinc-900 border-zinc-700 text-zinc-100 flex-1"
                data-testid="storefront-avatar-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Handle</Label>
            <Input value={profile.handle} onChange={(e) => setProfile({...profile, handle: e.target.value.toLowerCase()})}
                   placeholder="your-handle" className="bg-zinc-900 border-zinc-700 text-zinc-100"
                   data-testid="storefront-handle-input" />
            <div className="text-[10px] mono text-zinc-500 mt-1">a-z 0-9 _ - · 3–30 chars</div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Display name</Label>
            <Input value={profile.display_name} onChange={(e) => setProfile({...profile, display_name: e.target.value})}
                   className="bg-zinc-900 border-zinc-700 text-zinc-100"
                   data-testid="storefront-display-name-input" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Bio <span className="text-zinc-600 mono">(markdown ok)</span></Label>
            <Textarea rows={3} value={profile.bio} onChange={(e) => setProfile({...profile, bio: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 text-zinc-100"
                      data-testid="storefront-bio-input" />
          </div>
          {/* Niche tags chips */}
          <div>
            <Label className="text-zinc-300 text-xs">Niche tags <span className="text-zinc-600 mono">(max 10)</span></Label>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 ap-panel-2 p-2 min-h-[44px]"
                 data-testid="storefront-niche-tags">
              {profile.niche_tags.map((t) => (
                <span key={t}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-700/60 text-emerald-300 text-[11px] mono"
                      data-testid={`storefront-tag-${t}`}>
                  #{t}
                  <button onClick={() => removeTag(t)} className="hover:text-rose-400"
                          data-testid={`storefront-tag-remove-${t}`} aria-label={`remove ${t}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
                  else if (e.key === "Backspace" && !tagInput && profile.niche_tags.length)
                    removeTag(profile.niche_tags[profile.niche_tags.length - 1]);
                }}
                onBlur={addTag}
                placeholder={profile.niche_tags.length === 0 ? "add tag…" : ""}
                className="flex-1 min-w-[80px] bg-transparent outline-none text-zinc-100 text-[12px] mono py-0.5"
                data-testid="storefront-tag-input"
              />
            </div>
            <div className="text-[10px] mono text-zinc-500 mt-1">Press Enter or comma to add. Backspace to remove last.</div>
          </div>
          <Button onClick={save} disabled={saving}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                  data-testid="storefront-save-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save storefront"}
          </Button>
        </div>
        <div className="space-y-3">
          <Label className="text-zinc-300 text-xs">Your link-in-bio URL</Label>
          <div className="ap-panel-2 p-3 flex items-center gap-2" data-testid="storefront-link-snippet">
            <Input value={storeUrl} readOnly className="bg-zinc-950 border-zinc-700 text-emerald-300 mono text-xs flex-1"
                   data-testid="storefront-url-input" />
            <Button size="sm" variant="outline" onClick={() => {
              navigator.clipboard.writeText(storeUrl);
              toast.success("Copied!");
            }} className="border-zinc-700 bg-zinc-900 text-zinc-200" data-testid="storefront-copy-btn">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <a href={`/store/${profile.handle}`} target="_blank" rel="noreferrer"
               className="text-emerald-400 hover:text-emerald-300" data-testid="storefront-preview-link"
               title="Preview public storefront">
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          {/* Featured products picker */}
          <div>
            <Label className="text-zinc-300 text-xs">
              Featured products{" "}
              <span className="text-zinc-600 mono">({profile.featured_product_ids.length}/8)</span>
            </Label>
            {products.length === 0 ? (
              <div className="ap-panel-2 p-3 text-[11px] text-zinc-500 mt-1.5"
                   data-testid="storefront-featured-empty">
                No products yet — add some from the{" "}
                <a href="/app/products" className="text-emerald-400 hover:text-emerald-300">Products page</a>.
              </div>
            ) : (
              <div className="ap-panel-2 p-2 max-h-56 overflow-y-auto space-y-1 mt-1.5"
                   data-testid="storefront-featured-list">
                {products.slice(0, 50).map((p) => {
                  const sel = profile.featured_product_ids.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => toggleFeatured(p.id)}
                      className={`w-full flex items-center gap-2 p-1.5 rounded text-xs text-left transition-colors ${
                        sel ? "bg-emerald-500/10 border border-emerald-700/60 text-emerald-200"
                            : "hover:bg-zinc-900 border border-transparent text-zinc-300"
                      }`}
                      data-testid={`storefront-featured-toggle-${p.id}`}
                    >
                      <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                        sel ? "bg-emerald-500 border-emerald-500" : "border-zinc-700"
                      }`}>
                        {sel && <Check className="h-3 w-3 text-zinc-950" />}
                      </span>
                      <span className="flex-1 truncate">{p.title || p.name || p.id}</span>
                      {p.niche && (
                        <span className="text-[9px] mono text-zinc-500">{p.niche}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="text-[11px] text-zinc-500">
            <span className="text-amber-400 mono">Tip:</span> drop your storefront URL into your TikTok bio. Featured products appear in a pinned carousel at the top.
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------- TikTok connected accounts (Phase 5.1) -----------------
const EXPECTED_TIKTOK_HANDLES = [
  { handle: "the.daily.find", label: "TikTok @the.daily.find" },
  { handle: "my.daily.find6", label: "TikTok @my.daily.find6" },
  { handle: "todays.daily.find", label: "TikTok @todays.daily.find" },
];

function TikTokAccountsSection() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tiktok/accounts`);
      setData(r.data);
    } catch (_) { setData({ accounts: [], configured: false }); }
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("ttk_connected")) {
      toast.success("TikTok account connected");
      window.history.replaceState({}, "", "/app/settings");
      // Re-fetch shortly after to pick up the freshly-persisted row in case
      // Mongo had a moment of replication lag on first read.
      setTimeout(load, 800);
    } else if (params.get("ttk_error")) {
      toast.error(`TikTok connect failed: ${params.get("ttk_error")}`);
      window.history.replaceState({}, "", "/app/settings");
    }
  }, [load]);

  const startOAuth = () => {
    window.location.href = `${API}/tiktok/oauth/start`;
  };

  const disconnect = async (id) => {
    if (!window.confirm("Disconnect this TikTok account?")) return;
    try {
      await axios.delete(`${API}/tiktok/accounts/${id}`);
      toast.success("Disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  if (!data) return null;
  const accounts = data.accounts || [];

  // Match a connected account to one of the 3 expected TikTok handles.
  // Priority: exact `username` match (most reliable — TikTok @handle field).
  // Fallback: fuzzy match on `display_name` after stripping dots / lowercasing.
  // This fixes the Phase 5.1c regression where display_name="Matt's TikTok"
  // would fail to match handle "the.daily.find" and render as not-connected.
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const findByHandle = (h) => {
    const normalizedHandle = norm(h);
    // 1) exact username match
    let m = accounts.find((a) => norm(a.username) === normalizedHandle);
    if (m) return m;
    // 2) fuzzy display_name (legacy fallback)
    return accounts.find((a) => {
      const dn = norm(a.display_name);
      return dn && (dn === normalizedHandle || dn.includes(normalizedHandle));
    });
  };

  const matchedIds = new Set(
    EXPECTED_TIKTOK_HANDLES.map((h) => findByHandle(h.handle)?.id).filter(Boolean)
  );
  const unmatched = accounts.filter((a) => !matchedIds.has(a.id));

  return (
    <section className="ap-panel p-5" data-testid="tiktok-accounts-section">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Music className="h-4 w-4 text-rose-400" />
          TikTok Accounts
          <span className={`text-[10px] mono px-1.5 py-0.5 rounded ml-1 ${
            data.environment === "sandbox"
              ? "bg-amber-500/15 text-amber-300 border border-amber-700/60"
              : "bg-emerald-500/15 text-emerald-300 border border-emerald-700/60"
          }`}>{data.environment || "—"}</span>
        </h2>
        <Button size="sm" onClick={startOAuth} disabled={!data.configured}
                className="bg-rose-500 hover:bg-rose-400 text-zinc-950 font-semibold text-xs"
                data-testid="tiktok-connect-btn">
          <Music className="h-3 w-3 mr-1.5" /> Connect TikTok Account
        </Button>
      </div>
      {!data.configured && (
        <div className="text-xs text-amber-200 mb-3 mono">
          ! TIKTOK_CLIENT_KEY / TIKTOK_SANDBOX_CLIENT_KEY not set in backend env.
        </div>
      )}
      <div className="space-y-2">
        {EXPECTED_TIKTOK_HANDLES.map((h) => {
          const acc = findByHandle(h.handle);
          return (
            <div key={h.handle}
                 className="ap-panel-2 p-3 flex items-center justify-between gap-2"
                 data-testid={`tiktok-card-${h.handle}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {acc?.avatar_url
                  ? <img src={acc.avatar_url} alt="" className="h-8 w-8 rounded-full" />
                  : <div className="h-8 w-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-500">?</div>}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">
                    {acc?.display_name || h.label}
                  </div>
                  <div className="text-[11px] mono text-zinc-500 truncate">
                    @{acc?.username || h.handle}{acc?.follower_count ? ` · ${acc.follower_count.toLocaleString()} followers` : ""}
                  </div>
                </div>
              </div>
              {acc ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <Badge variant="outline" className="border-emerald-700/60 bg-emerald-500/10 text-emerald-300 text-[10px]">
                    <Check className="h-2.5 w-2.5 mr-1" /> connected
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => disconnect(acc.id)}
                          className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                          data-testid={`tiktok-disconnect-${h.handle}`}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px]">
                  not connected
                </Badge>
              )}
            </div>
          );
        })}

        {/* Connected accounts that don't fit the 3 expected slots (Phase 5.1c
            fix — TikTok's display_name doesn't always match the @handle, and
            we still want the row to surface so the user knows it's connected). */}
        {unmatched.length > 0 && (
          <div className="pt-2 mt-2 border-t border-zinc-800 space-y-2"
               data-testid="tiktok-unmatched-accounts">
            <div className="text-[10px] mono uppercase text-zinc-600">
              Other connected accounts ({unmatched.length})
            </div>
            {unmatched.map((acc) => (
              <div key={acc.id}
                   className="ap-panel-2 p-3 flex items-center justify-between gap-2"
                   data-testid={`tiktok-unmatched-${acc.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {acc.avatar_url
                    ? <img src={acc.avatar_url} alt="" className="h-8 w-8 rounded-full" />
                    : <div className="h-8 w-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-500">?</div>}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {acc.display_name || acc.username || "Connected TikTok"}
                    </div>
                    <div className="text-[11px] mono text-zinc-500 truncate">
                      {acc.username ? `@${acc.username}` : `open_id ${acc.open_id?.slice(0, 12)}…`}
                      {acc.follower_count ? ` · ${acc.follower_count.toLocaleString()} followers` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Badge variant="outline" className="border-emerald-700/60 bg-emerald-500/10 text-emerald-300 text-[10px]">
                    <Check className="h-2.5 w-2.5 mr-1" /> connected
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => disconnect(acc.id)}
                          className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs">
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="text-[10px] mono text-zinc-600 mt-3">
        Redirect URI: <span className="text-emerald-400">{data.redirect_uri}</span>
      </div>
    </section>
  );
}

